import { Link, useLocation } from 'react-router-dom';
import {
  LayoutDashboard,
  Key,
  Users,
  Package,
  Building2,
  UserPlus,
  FileText,
  Cpu,
  ScrollText,
  Globe,
  Shield,
  CheckCircle2,
  PackageCheck,
  BarChart3,
  Activity,
  Bell,
  Webhook,
  CreditCard,
  Settings,
  ShieldCheck,
  LogOut
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

interface SidebarProps {
  onLogout: () => void;
  user?: any;
}

type UserRole = 'OWNER' | 'ADMIN' | 'MEMBER' | 'VIEWER';

interface NavItem {
  name: string;
  path: string;
  icon: any;
  allowedRoles?: UserRole[]; // If not specified, available to all authenticated users
}

interface NavSection {
  title: string;
  items: NavItem[];
}

const navigation: NavSection[] = [
  {
    title: 'Overview',
    items: [
      { name: 'Dashboard', path: '/', icon: LayoutDashboard },
    ],
  },
  {
    title: 'License Management',
    items: [
      { name: 'Licenses', path: '/licenses', icon: Key },
      { name: 'Activations', path: '/activations', icon: CheckCircle2, allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
      { name: 'Verification Packages', path: '/verification', icon: PackageCheck, allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
    ],
  },
  {
    title: 'Users & Access',
    items: [
      { name: 'Users', path: '/users', icon: Users, allowedRoles: ['OWNER', 'ADMIN'] },
      { name: 'Roles & Permissions', path: '/roles', icon: Shield, allowedRoles: ['OWNER', 'ADMIN'] },
      { name: 'API Keys', path: '/api-keys', icon: Key, allowedRoles: ['OWNER', 'ADMIN'] },
    ],
  },
  {
    title: 'Catalog',
    items: [
      { name: 'Products', path: '/products', icon: Package },
      { name: 'Customers', path: '/customers', icon: Building2, allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
    ],
  },
  {
    title: 'Partners',
    items: [
      { name: 'Partners', path: '/partners', icon: UserPlus, allowedRoles: ['OWNER', 'ADMIN'] },
      { name: 'Purchase Orders', path: '/purchase-orders', icon: FileText, allowedRoles: ['OWNER', 'ADMIN'] },
    ],
  },
  {
    title: 'Configuration',
    items: [
      { name: 'Organizations', path: '/organizations', icon: Building2, allowedRoles: ['OWNER', 'ADMIN'] },
      { name: 'Domains', path: '/domains', icon: Globe, allowedRoles: ['OWNER', 'ADMIN'] },
      { name: 'Webhooks', path: '/webhooks', icon: Webhook, allowedRoles: ['OWNER', 'ADMIN'] },
    ],
  },
  {
    title: 'Billing',
    items: [
      { name: 'Billing', path: '/billing', icon: CreditCard, allowedRoles: ['OWNER', 'ADMIN'] },
    ],
  },
  {
    title: 'Insights',
    items: [
      { name: 'Analytics', path: '/analytics', icon: BarChart3, allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
      { name: 'System Logs', path: '/system-logs', icon: ScrollText, allowedRoles: ['OWNER', 'ADMIN'] },
      { name: 'Activity Logs', path: '/activity-logs', icon: Activity, allowedRoles: ['OWNER', 'ADMIN', 'MEMBER'] },
    ],
  },
  {
    title: 'Security',
    items: [
      { name: 'System Signatures', path: '/system-signatures', icon: Cpu, allowedRoles: ['OWNER', 'ADMIN'] },
      { name: 'MFA', path: '/mfa', icon: ShieldCheck },
    ],
  },
  {
    title: 'Account',
    items: [
      { name: 'Settings', path: '/settings', icon: Settings },
      { name: 'Notifications', path: '/notifications', icon: Bell },
    ],
  },
];

function Sidebar({ onLogout, user }: SidebarProps) {
  const location = useLocation();

  // Helper function to check if user has access to a nav item
  const hasAccess = (item: NavItem): boolean => {
    // If no role restrictions, accessible to all authenticated users
    if (!item.allowedRoles || item.allowedRoles.length === 0) {
      return true;
    }
    // If user or role not available, deny access by default
    if (!user || !user.role) {
      return false;
    }
    // Check if user's role is in the allowed roles
    return item.allowedRoles.includes(user.role);
  };

  // Filter navigation sections to only show items the user has access to
  const filteredNavigation = navigation
    .map((section) => ({
      ...section,
      items: section.items.filter(hasAccess),
    }))
    .filter((section) => section.items.length > 0); // Only show sections with at least one item

  return (
    <div className="flex h-screen w-64 flex-col border-r bg-card">
      <div className="flex h-16 items-center border-b px-6">
        <Shield className="h-6 w-6 text-primary mr-2" />
        <h1 className="text-lg font-bold">License Management</h1>
      </div>

      <nav className="flex-1 overflow-y-auto p-4 space-y-6">
        {filteredNavigation.map((section) => (
          <div key={section.title}>
            <h2 className="px-3 mb-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              {section.title}
            </h2>
            <div className="space-y-1">
              {section.items.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;

                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors',
                      isActive
                        ? 'bg-primary text-primary-foreground'
                        : 'text-foreground/60 hover:bg-accent hover:text-foreground'
                    )}
                  >
                    <Icon className="h-4 w-4" />
                    {item.name}
                  </Link>
                );
              })}
            </div>
          </div>
        ))}
      </nav>

      <div className="border-t p-4">
        <Button
          onClick={onLogout}
          variant="ghost"
          className="w-full justify-start"
        >
          <LogOut className="h-4 w-4 mr-3" />
          Logout
        </Button>
      </div>
    </div>
  );
}

export default Sidebar;
